import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import Icon from './Icon';
import ThemeToggle from './ThemeToggle';
import './Sidebar.css';

const NAV = [
  { label: 'Tableau de bord', iconName: 'accueil',    path: '/', exact: true },
  {
    label: 'Production', iconName: 'fumage',
    children: [
      { label: 'Saumon Fumé',     path: '/production/saumon' },
      { label: 'Côtes Levées',    path: '/production/ribs' },
      { label: 'Porc Effiloché',  path: '/production/porc' },
      { label: 'Brisket',         path: '/production/brisket' },
      { label: 'Saucisses',       path: '/production/saucisses' },
      { label: 'Sauce BBQ',       path: '/production/sauce' },
      { label: 'Tallow (Suif)',   path: '/production/tallow' },
      { label: 'Températures',    path: '/temperatures' },
      { label: 'Analyse comparative', path: '/temperatures/analyse' },
    ],
  },
  {
    label: 'Inventaire', iconName: 'package',
    children: [
      { label: 'Matières premières', path: '/inventaire/matieres' },
      { label: 'Produits finis',     path: '/inventaire/produits' },
    ],
  },
  { label: 'Catalogue',  iconName: 'boutique', path: '/catalogue' },
  { label: 'Événements', iconName: 'date',     path: '/evenements' },
  {
    label: 'Corporatif', iconName: 'entreprise',
    children: [
      { label: 'Contrats',           path: '/corpo' },
      { label: 'Clients',            path: '/corpo/clients' },
      { label: 'Termes & Conditions',path: '/corpo/termes' },
      { label: 'Liste de prix',       path: '/corpo/prix-catalogue' },
    ],
  },
  { label: 'Commandes',  iconName: 'panier',    path: '/commandes' },
  { label: 'Analytique', iconName: 'analytics', path: '/analytics' },
  {
    label: 'Communications', iconName: 'email',
    children: [
      { label: 'Emails', path: '/emails' },
    ],
  },
  {
    label: 'Comptabilité', iconName: 'encaisse',
    children: [
      { label: 'Journal & Encaisse',    path: '/comptabilite/journal' },
      { label: 'État des résultats',    path: '/comptabilite/resultats' },
      { label: 'Taxes TPS/TVQ',         path: '/comptabilite/taxes' },
      { label: 'Dépenses déductibles',  path: '/comptabilite/depenses-perso' },
      { label: 'Avances de fonds',      path: '/comptabilite/avances' },
      { label: 'Registre des actifs',   path: '/comptabilite/actifs' },
    ],
  },
];

const NAV_BOTTOM = [
  { label: 'Agent Debug', iconName: 'recherche',  path: '/agent-debug' },
  { label: 'Paramètres',  iconName: 'parametres', path: '/settings', badgeKey: 'alertes' },
];

export default function Sidebar({ isOpen, onClose, nbAlertes = 0 }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState({ Production: true });

  const userRaw = localStorage.getItem('escamel_user');
  const user = userRaw ? JSON.parse(userRaw) : null;

  function handleLogout() {
    localStorage.removeItem('escamel_token');
    localStorage.removeItem('escamel_user');
    navigate('/login', { replace: true });
  }

  const toggle = (label) =>
    setOpen(prev => ({ ...prev, [label]: !prev[label] }));

  const isActive = (children) =>
    children?.some(c => location.pathname.startsWith(c.path));

  return (
    <aside className={`sidebar${isOpen ? ' sidebar-open' : ''}`}>
      {/* Logo */}
      <div className="sidebar-logo">
        <img src="/logo.png" alt="Escamel" className="logo-img" />
        <div style={{ flex: 1 }}>
          <div className="logo-title">ESCAMEL</div>
          <div className="logo-sub">Artisans du Fumoir</div>
        </div>
        <button className="sidebar-close" onClick={onClose} aria-label="Fermer le menu">
          <Icon name="fermer" size={16} />
        </button>
      </div>

      {/* Nav */}
      <nav className="sidebar-nav">
        {NAV.map(item => (
          <div key={item.label} className="nav-item">
            {item.children ? (
              <>
                <button
                  className={`nav-group-btn ${isActive(item.children) ? 'active-group' : ''}`}
                  onClick={() => toggle(item.label)}
                >
                  <span className="nav-icon"><Icon name={item.iconName} size={16} /></span>
                  <span>{item.label}</span>
                  <span className={`nav-chevron ${open[item.label] ? 'open' : ''}`}>
                    <Icon name="chevron" size={14} />
                  </span>
                </button>
                {open[item.label] && (
                  <div className="nav-children">
                    {item.children.map(child => (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        className={({ isActive }) =>
                          `nav-child-link ${isActive ? 'active' : ''}`
                        }
                        onClick={onClose}
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <NavLink
                to={item.path}
                end={item.exact}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={onClose}
              >
                <span className="nav-icon"><Icon name={item.iconName} size={16} /></span>
                <span>{item.label}</span>
              </NavLink>
            )}
          </div>
        ))}

        <div className="sidebar-sep" />
        {NAV_BOTTOM.map(item => (
          <div key={item.label} className="nav-item">
            <NavLink
              to={item.path}
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              onClick={onClose}
            >
              <span className="nav-icon"><Icon name={item.iconName} size={16} /></span>
              <span>{item.label}</span>
              {item.badgeKey === 'alertes' && nbAlertes > 0 && (
                <span className="nav-badge-alertes">{nbAlertes}</span>
              )}
            </NavLink>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="sidebar-footer">
        <ThemeToggle />
        <div className="footer-user">
          <div className="user-avatar">
            {user ? user.username.toUpperCase().slice(0, 2) : '??'}
          </div>
          <div style={{ flex: 1 }}>
            <div className="user-name">{user ? user.nom : '—'}</div>
            <div className="user-role muted">Associé</div>
          </div>
          <button className="logout-btn" onClick={handleLogout} title="Déconnexion" aria-label="Déconnexion">
            <Icon name="deconnexion" size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}
